import { useQuery } from '@hooks/reactQuery';

import {
	getConnectionLogsAvailableUsers,
	getConnectionLogsConnectionStatuses,
	getConnectionLogsEventTypes,
	getConnectionLogsMiddlewares,
	getConnectionLogsSources,
} from '@api/middlewareSMO';

import { mapDropdownOptions, optionsFormatted } from '@utils/formatter';

import { TNameTitle } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IConnectionLogsFilterMiddleware, IConnectionLogsFilterUser } from '@interfaces/middlewareSMO/connectionLogs';

import { EUserRoles } from '@enums/common';

import { refetchOptions } from '@constants/common';
import {
	MIDDLEWARE_SMO_CONNECTION_LOGS_CONNECTION_STATUSES,
	MIDDLEWARE_SMO_CONNECTION_LOGS_EVENT_TYPES,
	MIDDLEWARE_SMO_CONNECTION_LOGS_MIDDLEWARES,
	MIDDLEWARE_SMO_CONNECTION_LOGS_SOURCES,
	MIDDLEWARE_SMO_CONNECTION_LOGS_USERS,
} from '@constants/queryKeys';

interface IUseQueryConnectionLogsFiltersReturned {
	users: TSelectOption<string>[];
	connectionStatuses: TSelectOption<string>[];
	eventTypes: TSelectOption<string>[];
	middlewares: TSelectOption<string>[];
	sources: TSelectOption<string>[];
	isLoadingUsers: boolean;
	isLoadingConnectionStatuses: boolean;
	isLoadingEventTypes: boolean;
	isLoadingMiddlewares: boolean;
	isLoadingSources: boolean;
}

export const useQueryConnectionLogsFilters = (): IUseQueryConnectionLogsFiltersReturned => {
	const {
		data: users,
		isInitialLoading: isLoadingUsers,
	} = useQuery<IConnectionLogsFilterUser[], TSelectOption<string>[]>({
		queryKey: [MIDDLEWARE_SMO_CONNECTION_LOGS_USERS],
		queryFn: getConnectionLogsAvailableUsers,
		select: (data) => mapDropdownOptions({
			items: data,
			valueKey: 'id',
			labelKey: 'name',
		}),
		allowedRoles: [EUserRoles.smoLightViewer, EUserRoles.smoLightUpdater],
		...refetchOptions,
	});

	const {
		data: connectionStatuses,
		isInitialLoading: isLoadingConnectionStatuses,
	} = useQuery<TNameTitle[], TSelectOption<string>[]>({
		queryKey: [MIDDLEWARE_SMO_CONNECTION_LOGS_CONNECTION_STATUSES],
		queryFn: getConnectionLogsConnectionStatuses,
		select: (data) => optionsFormatted<TNameTitle>(data),
		allowedRoles: [EUserRoles.smoLightViewer, EUserRoles.smoLightUpdater],
		...refetchOptions,
	});

	const {
		data: eventTypes,
		isInitialLoading: isLoadingEventTypes,
	} = useQuery<TNameTitle[], TSelectOption<string>[]>({
		queryKey: [MIDDLEWARE_SMO_CONNECTION_LOGS_EVENT_TYPES],
		queryFn: getConnectionLogsEventTypes,
		select: (data) => optionsFormatted<TNameTitle>(data),
		allowedRoles: [EUserRoles.smoLightViewer, EUserRoles.smoLightUpdater],
		...refetchOptions,
	});

	const {
		data: middlewares,
		isInitialLoading: isLoadingMiddlewares,
	} = useQuery<IConnectionLogsFilterMiddleware[], TSelectOption<string>[]>({
		queryKey: [MIDDLEWARE_SMO_CONNECTION_LOGS_MIDDLEWARES],
		queryFn: getConnectionLogsMiddlewares,
		select: (data) => mapDropdownOptions({
			items: data,
			valueKey: 'id',
			labelKey: 'systemId',
		}),
		allowedRoles: [EUserRoles.smoLightViewer, EUserRoles.smoLightUpdater],
		...refetchOptions,
	});

	const {
		data: sources,
		isInitialLoading: isLoadingSources,
	} = useQuery<TNameTitle[], TSelectOption<string>[]>({
		queryKey: [MIDDLEWARE_SMO_CONNECTION_LOGS_SOURCES],
		queryFn: getConnectionLogsSources,
		select: (data) => optionsFormatted<TNameTitle>(data),
		allowedRoles: [EUserRoles.smoLightViewer, EUserRoles.smoLightUpdater],
		...refetchOptions,
	});

	return {
		users: users || [],
		connectionStatuses: connectionStatuses || [],
		eventTypes: eventTypes || [],
		middlewares: middlewares || [],
		sources: sources || [],
		isLoadingUsers,
		isLoadingConnectionStatuses,
		isLoadingEventTypes,
		isLoadingMiddlewares,
		isLoadingSources,
	};
};
